import Link from "@mui/material/Link";
import { ReactNode } from "react";

// Also a literal "\n", for values set on a single line, e.g. through `gh variable set`.
const LINE_BREAK_PATTERN = /\r?\n|\\n/;
// [label](url), **bold**, or a bare http(s) URL whose trailing (German) punctuation stays text.
const INLINE_TOKEN_PATTERN =
  /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|(https?:\/\/[^\s<>()[\]]*[^\s<>()[\].,;:!?"'“”„‘’«»…])/g;
const ALLOWED_SCHEMES = ["http:", "https:", "mailto:"];

const isAllowedUrl = (url: string): boolean => {
  try {
    return ALLOWED_SCHEMES.includes(new URL(url).protocol);
  } catch {
    return false;
  }
};

const parseLine = (line: string, keyPrefix: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of line.matchAll(INLINE_TOKEN_PATTERN)) {
    const [fullMatch, linkLabel, linkUrl, boldLabel, bareUrl] = match;
    const key = `${keyPrefix}-${match.index}`;
    const href = bareUrl ?? linkUrl;

    if (match.index > lastIndex) {
      nodes.push(line.slice(lastIndex, match.index));
    }

    if (boldLabel !== undefined) {
      nodes.push(<strong key={key}>{parseLine(boldLabel, key)}</strong>);
    } else if (isAllowedUrl(href)) {
      nodes.push(
        // Inherited colour: the theme green reaches only 2.4:1 contrast on the warning background.
        <Link key={key} href={href} target="_blank" rel="noopener noreferrer" color="inherit" sx={{ fontWeight: 700 }}>
          {linkLabel ?? bareUrl}
        </Link>,
      );
    } else {
      // Kept as typed, so whoever configured a rejected scheme sees it on the page.
      nodes.push(fullMatch);
    }

    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < line.length) {
    nodes.push(line.slice(lastIndex));
  }

  return nodes;
};

/** Turns the configured notice into React nodes: line breaks, `**bold**`, `[label](url)` and bare URLs. */
export const parseNoticeText = (text: string): ReactNode[] =>
  text
    .split(LINE_BREAK_PATTERN)
    .flatMap((line, index) => [
      ...(index > 0 ? [<br key={`br-${index}`} />] : []),
      ...parseLine(line, `line-${index}`),
    ]);
